import { useEffect } from "react";
import {
  Search, ShoppingBag, MapPin, ShieldCheck, CreditCard, Star,
  Rocket, Check, Compass, PiggyBank, Link2, BadgeCheck,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import AppStoreBadge from "@/components/site/AppStoreBadge";
import PlayStoreBadge from "@/components/site/PlayStoreBadge";
import PhoneFrame from "@/components/site/PhoneFrame";

const steps = [
  { icon: Search, title: "Discover", body: "Browse food creators and dishes near you — filter by cuisine, dietary needs, pickup or delivery, and see who's open now." },
  { icon: ShoppingBag, title: "Order", body: "Pick your time (ASAP or scheduled), add extras and sides, pay securely. Creators confirm and tell you exactly when it's ready." },
  { icon: MapPin, title: "Track & enjoy", body: "Watch your delivery approach on a live map, or share when you're on your way for pickup. Hand off with a 4-digit code — no fuss." },
];

const featureCards = [
  { img: "/screenshots/raw_04.webp", title: "Food creator pages", body: "Photos, hours, reviews and every dish — with real ratings from real orders." },
  { img: "/screenshots/raw_05.webp", title: "Clear order updates", body: "One fixed ready time, live status, chat with your creator until handoff." },
  { img: "/screenshots/raw_tracking.webp", title: "Live delivery map", body: "See your creator on the way and exactly when they'll arrive." },
  { img: "/screenshots/raw_02.webp", title: "Know what you're eating", body: "Ingredients, allergens, serving size and storage on every dish — made to order or ready now." },
  { img: "/screenshots/raw_03.webp", title: "Pickup or delivery", body: "Choose a time, tip your food creator and pay securely. Party platters for a crowd, too." },
  { img: "/screenshots/raw_06.webp", title: "Talk to your food creator", body: "Message directly about extras, timing or allergies until the order is handed off." },
];

const consumerPerks = [
  { icon: Compass, title: "Discover", body: "Find independent local food that may not appear on the big delivery apps — home kitchens, food trucks, pop-ups, local restaurants — even book select private chefs to cook at your home." },
  { icon: PiggyBank, title: "Save", body: "As a customer, your service fee is just $0.99 for your first 7 days after your first completed order. Then a simple 5% — always between $1.50 and $4.99, with creator-set delivery fees shown before checkout." },
  { icon: BadgeCheck, title: "Trust", body: "See honest reviews and applicable verified permits, licenses and certifications for each creator's business type." },
];

const creatorBenefits = [
  "Get discovered by new customers in the Dishly marketplace",
  "Take direct orders from your existing customers with your own link",
  "Manage orders: Needs action · In the kitchen · Ready · Out for delivery",
  "Manage listings and inventory in minutes",
  "Track reviews and reply to customers",
  "View analytics and insights on what's selling",
  "Manage payouts — paid straight to your bank",
  "Promote dishes and your business with boosts",
  "Stay current on applicable permits and verification for your business type",
];

const consumerFaqs = [
  { q: "How much is delivery?", a: "Delivery fees are set by each creator and shown before you check out — no surprises. Pickup is always free if you'd rather swing by." },
  { q: "Can I book a private chef or caterer?", a: "Yes — select creators offer services like private chef experiences, catering and party platters. Browse their page, pick a date, and book right in the app." },
  { q: "What if something goes wrong with my order?", a: "Message your creator in the app first — most things get sorted in minutes. If not, Dishly support steps in and refunds are handled automatically where they apply." },
  { q: "Is my payment safe?", a: "Always. Cards are processed by Stripe — Dishly never sees your card number, and your money moves only according to your order's status." },
  { q: "Do I need the app to order?", a: "To browse the marketplace, yes — iPhone or Android. But when a creator shares their personal Dishly link, you can order straight from the web, no download needed." },
];

const feeFaqs = [
  { q: "When does the 0% commission end?", a: "Fourteen days after your creator account is approved. Every order in that window is commission-free — you keep the full sale." },
  { q: "What's the difference between direct-link and marketplace orders?", a: "Share your personal Dishly link (Instagram, TikTok, text, QR code) and pay just 5% on those orders. When a new customer discovers you in the Dishly marketplace, that order is 10%." },
  { q: "Do I really keep all my tips and delivery fees?", a: "Yes — 100% of tips and 100% of the delivery fees you set go straight to you, on every order, forever." },
  { q: "Is there a monthly fee?", a: "Not on the Standard plan. You only pay commission when you make a sale — no subscription, no contracts." },
  { q: "What do my customers pay?", a: "A small capped service fee: $0.99 for their first 7 days, then 5% — always between $1.50 and $4.99, shown before they check out." },
  { q: "How do I get paid?", a: "Payouts go straight to your bank via Stripe. You can track every payout and your earnings in the app's Insights tab." },
];

const trustItems = [
  { icon: ShieldCheck, title: "Verified creators", body: "Dishly reviews applicable permits, licenses and certifications based on the creator's business type and location — with expiry reminders." },
  { icon: CreditCard, title: "Secure payments", body: "Cards are handled by Stripe — Dishly never sees your card number. Deposits and refunds are automatic." },
  { icon: Star, title: "Honest reviews", body: "Only customers who completed an order can rate, one review per creator, and creators can reply." },
];

export default function Landing() {
  useEffect(() => {
    document.title = "Dishly — Real food. Made local.";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-cream text-charcoal" data-testid="landing-page">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden pt-24 pb-14 lg:pt-32 lg:pb-20">
        <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 px-5 lg:grid-cols-[1.05fr_0.95fr] lg:px-8">
          <div>
            <span className="inline-flex items-center rounded-full bg-terracotta/10 px-3 py-1 text-[12px] font-bold text-terracotta">
              Now on the App Store & Google Play
            </span>
            <h1 className="mt-5 text-4xl font-black leading-[1.05] tracking-tight text-charcoal sm:text-5xl lg:text-[56px]">
              Real food. Made local.
            </h1>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-charcoal/70 sm:text-lg">
              Discover real food from independent local creators and businesses — home cooks,
              bakers, food trucks, private chefs, coffee stands, meal prep, caterers, local
              restaurants & more.
            </p>
            <p className="mt-3 max-w-lg text-[15px] leading-relaxed text-charcoal/60">
              Order pickup or delivery, book select services, and support local — all through Dishly.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <a href="#get-dishly" data-testid="hero-order-btn"
                className="rounded-full bg-terracotta px-7 py-3.5 text-sm font-bold uppercase tracking-wide text-white shadow-lg shadow-terracotta/25 transition-transform hover:scale-[1.03] active:scale-95">
                Order on Dishly
              </a>
              <a href="#for-creators" data-testid="hero-sell-btn"
                className="rounded-full border-2 border-charcoal px-7 py-3 text-sm font-bold uppercase tracking-wide text-charcoal transition-colors hover:bg-charcoal hover:text-cream">
                Start Selling
              </a>
            </div>
            <div className="mt-6">
              <div className="flex flex-wrap items-center gap-3">
                <AppStoreBadge size="md" testId="hero-app-store-btn" />
                <PlayStoreBadge size="md" testId="hero-play-store-btn" />
              </div>
            </div>
          </div>

          {/* single phone */}
          <div className="flex justify-center" data-testid="hero-phone">
            <PhoneFrame
              src="/screenshots/raw_01.webp"
              alt="Discover food creators near you"
              ratio="926 / 2097"
              className="w-full max-w-[260px] sm:max-w-[320px]"
            />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-14 sm:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="rounded-3xl border border-black/5 bg-cream-deep p-6 sm:p-10">
            <h2 className="text-2xl font-black tracking-tight text-charcoal sm:text-3xl">How it works</h2>
            <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
              {steps.map((s) => (
                <div key={s.title} className="rounded-2xl border border-black/5 bg-white p-6">
                  <span className="grid h-11 w-11 place-items-center rounded-full bg-terracotta/10 text-terracotta">
                    <s.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 text-lg font-bold text-charcoal">{s.title}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-charcoal/65">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section id="features" className="py-14 sm:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <h2 className="text-3xl font-black tracking-tight text-charcoal sm:text-4xl">Built for real authentic food</h2>
          <p className="mt-3 max-w-2xl text-base text-charcoal/65 sm:text-lg">
            Everything in the app, from your first browse to the last bite.
          </p>
          <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {featureCards.map((f) => (
              <div key={f.title} className="flex h-full flex-col">
                <div className="flex min-h-0 flex-1 flex-col">
                  <PhoneFrame src={f.img} alt={f.title} testId={`feature-phone-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
                </div>
                <h3 className="mt-5 text-lg font-bold text-charcoal">{f.title}</h3>
                <p className="mt-1.5 text-[15px] leading-relaxed text-charcoal/65">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONSUMER PERKS: DISCOVER / SAVE / TRUST */}
      <section className="pb-14 sm:pb-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <p className="mb-6 text-center text-[12px] font-bold uppercase tracking-widest text-terracotta">For customers</p>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {consumerPerks.map((p) => (
              <div key={p.title} className="rounded-2xl border border-black/5 bg-white p-6" data-testid={`perk-${p.title.toLowerCase()}`}>
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-terracotta/10 text-terracotta">
                  <p.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-5 text-lg font-bold uppercase tracking-wide text-charcoal">{p.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-charcoal/65">{p.body}</p>
              </div>
            ))}
          </div>
          <p className="mt-5 text-center text-[13px] text-charcoal/50">
            Customer service fees help cover secure payment processing and platform costs.
          </p>

          {/* customer FAQ */}
          <div className="mx-auto mt-8 max-w-2xl" data-testid="customer-faq">
            <h3 className="text-center text-[12px] font-bold uppercase tracking-widest text-charcoal/45">Questions customers ask</h3>
            <Accordion type="single" collapsible className="mt-2">
              {consumerFaqs.map((f, i) => (
                <AccordionItem key={i} value={`customer-${i}`} className="border-black/5">
                  <AccordionTrigger data-testid={`customer-faq-q-${i}`} className="text-left text-[15px] font-bold text-charcoal hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[14px] leading-relaxed text-charcoal/65">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* FOR CREATORS */}
      <section id="for-creators" className="border-y border-black/5 bg-cream-deep py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <p className="text-[12px] font-bold uppercase tracking-widest text-terracotta">For all local food creators</p>
          <h2 className="mt-3 text-3xl font-black tracking-tight text-charcoal sm:text-4xl">Run and grow your food business with Dishly.</h2>

          <div className="mt-10 grid grid-cols-1 items-start gap-12 lg:grid-cols-[1.1fr_1fr]">
            <div>
              <p className="max-w-lg text-base leading-relaxed text-charcoal/70 sm:text-[17px]">
                Home cook, baker, food truck, coffee stand, meal prep, caterer, private chef,
                pop-up, market vendor or independent local restaurant — list your food in minutes,
                set your own hours, prices and pickup spots, and get paid straight to your bank.
              </p>
              <p className="mt-4 max-w-lg text-[15px] font-bold text-charcoal">
                Get discovered. Take orders. Grow your business.
              </p>
              <ul className="mt-5 space-y-3">
                {creatorBenefits.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-[15px] text-charcoal/80">
                    <span className="mt-1 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-terracotta text-white">
                      <Check className="h-3 w-3" />
                    </span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              {/* pricing */}
              <div className="mt-8 rounded-2xl border border-terracotta/20 bg-white p-6" data-testid="creator-pricing">
                <div className="flex items-center gap-2 text-terracotta">
                  <Rocket className="h-4 w-4" />
                  <span className="text-[12px] font-bold uppercase tracking-widest">Start free · 0% creator commission for your first 14 days</span>
                </div>
                <h3 className="mt-3 text-xl font-black tracking-tight text-charcoal">Pay less when you bring the customer.</h3>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="rounded-xl bg-terracotta/[0.07] p-4">
                    <p className="font-display text-3xl font-black text-terracotta">5%</p>
                    <p className="mt-1 text-[13px] font-semibold leading-snug text-charcoal/70">Orders through your personal Dishly direct link</p>
                  </div>
                  <div className="rounded-xl bg-black/[0.04] p-4">
                    <p className="font-display text-3xl font-black text-charcoal">10%</p>
                    <p className="mt-1 text-[13px] font-semibold leading-snug text-charcoal/70">Orders when customers discover you in the marketplace</p>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {["No monthly fee on Standard", "Keep 100% of tips", "Keep 100% of delivery fees"].map((t) => (
                    <span key={t} className="rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-bold text-emerald-700">{t}</span>
                  ))}
                </div>
              </div>

              {/* fee FAQ */}
              <div className="mt-6" data-testid="fee-faq">
                <h3 className="text-[12px] font-bold uppercase tracking-widest text-charcoal/45">Fee questions creators ask</h3>
                <Accordion type="single" collapsible className="mt-2">
                  {feeFaqs.map((f, i) => (
                    <AccordionItem key={i} value={`fee-${i}`} className="border-black/5">
                      <AccordionTrigger data-testid={`fee-faq-q-${i}`} className="text-left text-[15px] font-bold text-charcoal hover:no-underline">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-[14px] leading-relaxed text-charcoal/65">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>

              <div className="mt-7">
                <p className="mb-3 text-[12px] font-bold uppercase tracking-widest text-charcoal/45">Start selling on Dishly — get the app</p>
                <div className="flex flex-wrap items-center gap-3">
                  <AppStoreBadge size="md" testId="creators-app-store-btn" />
                  <PlayStoreBadge size="md" testId="creators-play-store-btn" />
                </div>
              </div>
            </div>

            <div className="flex justify-center" data-testid="creators-phone">
              <PhoneFrame
                src="/screenshots/raw_08.webp"
                alt="Run your food business from your phone"
                ratio="926 / 2097"
                className="w-full max-w-[260px] sm:max-w-[320px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* DIRECT LINK */}
      <section className="py-16 sm:py-20">
        <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 px-5 lg:grid-cols-2 lg:px-8">
          <div className="order-2 flex justify-center lg:order-1" data-testid="direct-link-phone">
            <PhoneFrame
              src="/screenshots/raw_04.webp"
              alt="A creator's personal Dishly storefront"
              className="w-full max-w-[260px] sm:max-w-[320px]"
            />
          </div>
          <div className="order-1 lg:order-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-terracotta/10 px-3 py-1 text-[12px] font-bold text-terracotta">
              <Link2 className="h-3.5 w-3.5" /> Direct ordering
            </span>
            <h2 className="mt-4 text-3xl font-black tracking-tight text-charcoal sm:text-4xl">
              Your customers. Your link. Lower commission.
            </h2>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-charcoal/70 sm:text-[17px]">
              Every creator gets a personalized Dishly storefront link they can share on Instagram,
              TikTok, Facebook, their website, text messages or QR codes. Customers can order
              directly from the web — no app download needed.
            </p>
            <div className="mt-6 inline-flex items-baseline gap-3 rounded-2xl border border-terracotta/20 bg-terracotta/[0.06] px-5 py-4">
              <span className="font-display text-3xl font-black text-terracotta">5%</span>
              <span className="text-[14px] font-semibold text-charcoal/75">commission on direct-link orders — vs 10% from the marketplace</span>
            </div>
            <p className="mt-5 max-w-lg text-[15px] leading-relaxed text-charcoal/65">
              And new customers can still find you through the Dishly marketplace — search,
              collections and local discovery keep working for you around the clock.
            </p>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="border-t border-black/5 bg-cream-deep py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <h2 className="text-3xl font-black tracking-tight text-charcoal sm:text-4xl">Safe for everyone at the table</h2>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {trustItems.map((t) => (
              <div key={t.title} className="rounded-2xl border border-black/5 bg-white p-6">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-terracotta/10 text-terracotta">
                  <t.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-5 text-lg font-bold text-charcoal">{t.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-charcoal/65">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="get-dishly" className="pb-20 pt-16 sm:pb-28 sm:pt-20">
        <div className="mx-auto max-w-4xl px-5 text-center lg:px-8">
          <h2 className="text-3xl font-black tracking-tight text-charcoal sm:text-4xl">
            Dinner's already cooking next door.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-charcoal/65 sm:text-lg">
            Order on Dishly — real food made local, on iPhone and Android.
          </p>
          <div className="mt-8 flex flex-col items-center gap-3">
            <div className="flex flex-wrap items-center justify-center gap-3">
              <AppStoreBadge size="lg" testId="cta-app-store-btn" />
              <PlayStoreBadge size="lg" testId="cta-play-store-btn" />
            </div>
            <span className="text-[13px] text-charcoal/50">Free to download for iPhone and Android</span>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
